import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";
import { API, errMsg } from "../lib/api";
import { gapTone } from "../lib/bopta";
import { VBCta } from "../components/VBCta";

const gapText = (m, s) => {
  if (m > s) return "The manager reports more autonomy than staff experience — delegated authority isn't translating into daily independence.";
  if (m < s) return "Staff experience more autonomy than the manager believes.";
  return "Perspectives align.";
};

const GapWidget = () => {
  const [m, setM] = useState(85);
  const [s, setS] = useState(25);
  const gap = Math.abs(m - s);
  return (
    <div className="panel p-7" data-testid="gap-widget">
      <p className="kicker">The 360° gap · illustrative</p>
      {[["Manager's self-assessment", m, setM, "gap-manager-slider"], ["Reporting staff average", s, setS, "gap-staff-slider"]].map(([l, v, f, t]) => (
        <div key={t} className="mt-5">
          <div className="flex justify-between text-sm"><span>{l}</span><b>{v}</b></div>
          <Slider className="mt-2" value={[v]} max={100} step={1} onValueChange={([x]) => f(x)} data-testid={t} />
        </div>
      ))}
      <p className="display mt-6 text-5xl font-extrabold" style={{ color: gapTone(gap) }} data-testid="gap-value">{gap}<span className="ml-2 text-base font-normal muted">point gap</span></p>
      <p className="mt-2 text-sm muted">{gapText(m, s)}</p>
    </div>
  );
};

const CreateTeam = () => {
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", org: "", manager_name: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await API.post("/teams", f);
      nav(`/team/${r.data.code}?admin=${r.data.admin_token}`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <form id="create" onSubmit={submit} className="panel space-y-3 p-7" data-testid="create-team-card">
      <p className="kicker">Team 360° dashboard</p>
      <h3 className="text-2xl font-bold">Create a team. No accounts needed.</h3>
      <p className="text-sm muted">You'll get a staff invite link, a manager self-assessment link and a private admin link.</p>
      <Input required placeholder="Team or department name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11" data-testid="team-name-input" />
      <Input placeholder="Company (optional)" value={f.org} onChange={(e) => setF({ ...f, org: e.target.value })} className="h-11" data-testid="team-org-input" />
      <Input placeholder="Manager's name (optional)" value={f.manager_name} onChange={(e) => setF({ ...f, manager_name: e.target.value })} className="h-11" data-testid="team-manager-input" />
      <button className="btn btn-main w-full" disabled={busy || !f.name.trim()} data-testid="team-create-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create team dashboard</button>
    </form>
  );
};

const PATHS = [
  ["Business acumen & commercial awareness", "For low [A] Administration or [B] Business: operational liquidity, basic P&L and ROI forecasting so managers can trust financial boundaries."],
  ["Executive communication & situational leadership", "For Rising Stars ready for promotion, or managers causing a bottleneck: move from directing tasks to coaching people."],
  ["Technical & systems mastery", "For low [T] Technology or [O] Operations: get institutional knowledge out of the manager's head and into the systems."],
];

export default function Employers() {
  return (
    <div className="wrap py-16">
      <p className="kicker">For founders, C-level & HR</p>
      <h1 className="mt-3 max-w-3xl text-4xl font-extrabold sm:text-5xl">Build a business that doesn't wait for permission.</h1>
      <p className="mt-4 max-w-2xl muted">Don't just ask whether managers delegate. Ask whether staff can act. A manager scoring 85 while their team averages 25 is a leadership fracture worth investigating — the system, not just the score.</p>
      <section className="mt-12 grid gap-6 lg:grid-cols-2">
        <CreateTeam />
        <GapWidget />
      </section>
      <section className="mt-16">
        <p className="kicker">Talent Positioning Suite · development pathways</p>
        <h2 className="mt-3 text-3xl font-bold">Job title ↔ competency ↔ authority.</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {PATHS.map(([t, d]) => <div key={t} className="panel p-6"><p className="font-bold">{t}</p><p className="mt-2 text-sm muted">{d}</p></div>)}
        </div>
      </section>
      <section className="mt-16 grid gap-6 lg:grid-cols-2">
        <VBCta title="Start with the founder lens." />
        <div className="panel flex flex-col justify-between p-8">
          <div><p className="kicker">Lead by example</p><h3 className="mt-2 text-2xl font-bold">Take the manager self-assessment.</h3><p className="mt-2 muted">Score how independently you believe your team works, then compare.</p></div>
          <Link to="/assessment?mode=manager" className="btn btn-main mt-6 self-start" data-testid="employers-manager-assessment">Manager self-assessment</Link>
        </div>
      </section>
    </div>
  );
}
